import React from 'react';
import type { Event, Organizer } from '../../types/index';
import { DetailsWithImage } from './DetailsWithImage';
import { DetailsWithoutImage } from './DetailsWithoutImage';
import type { DetailsProps } from './types';

export type MusicProps = {
    hasPresenter: boolean | null;
    presenter: string | null;
    hasAgeRestriction: boolean | null;
    ageRestriction: string | null;
};

export type EventHeroComponentProps = {
    displayPrice: string;
    croppedLogoUrl480: string;
    croppedLogoUrl600: string;
    croppedLogoUrl800: string;
    croppedLogoUrl940?: string;
    eventNameTruncated: string;
    shouldShowExternalTicketingProvider: boolean;
    externalTicketingProviderLabelText: string;
    shouldShowPriceOnHero: boolean;
    hasArtistPresentedByModule: boolean;
    hasPresenterOrAgeRestriction: boolean;
    gaCategory: string;
} & MusicProps;

type EventHeroEventProps = {
    eventName: Event['name'];
} & Pick<
    Event,
    | 'hasCroppedLogo'
    | 'isEnded'
    | 'isSeriesParent'
    | 'hideStartDate'
    | 'startDateTime'
    | 'startMonthAbbr'
    | 'startDay'
    | 'shouldDisplayRsvp'
>;

type EventHeroOrganizerProps = {
    organizationNameTruncated: Organizer['nameTruncated'];
    orgProfileLink: Organizer['url'];
    organizerId: Organizer['id'];
} & Pick<Organizer, 'displayOrganizationName'>;

export type EventHeroProps = EventHeroComponentProps &
    EventHeroEventProps &
    EventHeroOrganizerProps;

const Details = (props: DetailsProps) => {
    const { hasCroppedLogo } = props;
    const detailsClasses = hasCroppedLogo
        ? 'g-cell-lg-4-12'
        : 'listing-hero__detailed-info--no-image';

    return (
        <div
            className={`g-cell g-cell-1-1 g-cell--no-gutters listing-hero__detailed-info ${detailsClasses}`}
        >
            <ul className="listing-panel-actions hide-large" role="list">
                <li
                    className="fx--fade-in-down fx--duration-2 fx--delay-14 z-1"
                    id="listings-root__bookmark-mobile"
                ></li>
            </ul>
            {hasCroppedLogo ? (
                <DetailsWithImage {...props} />
            ) : (
                <DetailsWithoutImage {...props} />
            )}
        </div>
    );
};

export const EventHero = ({
    hasCroppedLogo,
    displayPrice,
    croppedLogoUrl480,
    croppedLogoUrl600,
    croppedLogoUrl800,
    eventNameTruncated,
    isSeriesParent,
    hideStartDate,
    startDateTime,
    startMonthAbbr,
    startDay,
    hasArtistPresentedByModule,
    hasPresenterOrAgeRestriction,
    hasPresenter,
    presenter,
    hasAgeRestriction,
    ageRestriction,
    eventName,
    displayOrganizationName,
    organizationNameTruncated,
    shouldDisplayRsvp,
    shouldShowExternalTicketingProvider,
    externalTicketingProviderLabelText,
    shouldShowPriceOnHero,
    orgProfileLink,
    organizerId,
    gaCategory,
}: EventHeroProps) => {
    const Image = () => {
        return (
            <div className="listing-hero listing-hero--ratio-one-two listing-hero--bkg clrfix fx--delay-6 fx--fade-in">
                <picture>
                    <source
                        srcSet={`
                            ${croppedLogoUrl480} 480w,
                            ${croppedLogoUrl600} 600w,
                            ${croppedLogoUrl800} 800w`}
                        sizes="
                            (max-width:480px) 480px,
                            (max-width:600px) 600px,
                            800px
                        "
                    />
                    <img
                        className="listing-hero-image listing-image--main"
                        width="600"
                        height="300"
                        alt={eventNameTruncated}
                        src={croppedLogoUrl600}
                    />
                </picture>
            </div>
        );
    };

    return (
        <div
            className={`listing-hero-details__main-container ${
                hasCroppedLogo ? 'fx--fade-in fx--delay-4' : ''
            }`}
        >
            <div className="g-group">
                <div
                    className={`g-cell g-cell-1-1 g-cell-lg-8-12 g-cell--no-gutters ${
                        hasCroppedLogo ? 'listing-hero--image-container' : ''
                    }`}
                >
                    {hasCroppedLogo && <Image />}
                </div>
                <Details
                    hideStartDate={hideStartDate}
                    hasCroppedLogo={hasCroppedLogo}
                    isSeriesParent={isSeriesParent}
                    startDateTime={startDateTime}
                    startMonthAbbr={startMonthAbbr}
                    startDay={startDay}
                    hasArtistPresentedByModule={hasArtistPresentedByModule}
                    hasPresenterOrAgeRestriction={hasPresenterOrAgeRestriction}
                    eventName={eventName}
                    eventNameTruncated={eventNameTruncated}
                    displayOrganizationName={displayOrganizationName}
                    shouldDisplayRsvp={shouldDisplayRsvp}
                    displayPrice={displayPrice}
                    shouldShowExternalTicketingProvider={
                        shouldShowExternalTicketingProvider
                    }
                    externalTicketingProviderLabelText={
                        externalTicketingProviderLabelText
                    }
                    orgProfileLink={orgProfileLink}
                    organizationNameTruncated={organizationNameTruncated}
                    hasPresenter={hasPresenter}
                    presenter={presenter}
                    hasAgeRestriction={hasAgeRestriction}
                    ageRestriction={ageRestriction}
                    shouldShowPriceOnHero={shouldShowPriceOnHero}
                    organizerId={organizerId}
                    gaCategory={gaCategory}
                />
            </div>
        </div>
    );
};
